import clsx from 'clsx'
import React, { ReactNode } from 'react'

import s from './styles.module.scss'

import { PingPongButtonTypeEnum } from '../utils/ping-pong-button-type.enum.ts'
import Button from '../../../../atoms/fields/Button/Button'
import {
  BUTTON_COLOR,
  BUTTON_WEIGHT,
} from '../../../../atoms/fields/Button/button.enum'
import Icon from '../../../../atoms/icons/icon/Icon'
import IconSvg from '../../../../atoms/icons/icon-svg/IconSvg'
import { useAdminCanUpdate } from '../../../../contexts/admin-can-update-context/useAdminCanUpdate'
import { useUserCanUpdateProposal } from '../../../../contexts/user-can-update-proposal-context/useUserCanUpdateProposal'

enum IconPositionEnum {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

interface IPingPongButtonData {
  type: PingPongButtonTypeEnum
  color: BUTTON_COLOR
  weight?: BUTTON_WEIGHT
  icon?: React.ReactNode
  iconPosition?: IconPositionEnum
  clickable?: boolean
  dashedBorder?: boolean
}

const PingPongButtonData: IPingPongButtonData[] = [
  {
    type: PingPongButtonTypeEnum.CONFIRM,
    color: BUTTON_COLOR.PRIMARY,
    icon: <IconSvg.ArrowRight color='currentColor' className={s.icon} />,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.LOAD_QUOTATION,
    color: BUTTON_COLOR.BLACK,
    weight: BUTTON_WEIGHT.OUTLINED,
    icon: <Icon name='paperclip' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.CONFIRM_BOOKING,
    color: BUTTON_COLOR.PRIMARY,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.MODIFY_QUOTATION,
    color: BUTTON_COLOR.PRIMARY,
    icon: <Icon name='paperclip' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.WAITING,
    color: BUTTON_COLOR.GREY,
    icon: <IconSvg.Loader color='currentColor' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: false,
  },
  {
    type: PingPongButtonTypeEnum.PAYMENT,
    color: BUTTON_COLOR.PRIMARY,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.GIVE_FEEDBACK,
    color: BUTTON_COLOR.PRIMARY,
    icon: <Icon name='star' size={13} className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.ADMIN_PREVIOUS_STEP,
    color: BUTTON_COLOR.GREEN,
    icon: <IconSvg.ArrowLeft color='currentColor' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.SUPER_ADMIN_PREVIOUS_STEP,
    color: BUTTON_COLOR.RED,
    icon: <IconSvg.ArrowLeft color='currentColor' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.ADMIN_NEXT_STEP,
    color: BUTTON_COLOR.GREEN,
    icon: <IconSvg.ArrowRight color='currentColor' className={s.icon} />,
    iconPosition: IconPositionEnum.RIGHT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.SUPER_ADMIN_NEXT_STEP,
    color: BUTTON_COLOR.RED,
    icon: <IconSvg.ArrowRight color='currentColor' className={s.icon} />,
    iconPosition: IconPositionEnum.RIGHT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.PAYMENT_DONE,
    color: BUTTON_COLOR.GREEN,
    weight: BUTTON_WEIGHT.OUTLINED,
    icon: <Icon name='check-circle-outline' className={s.icon} />,
    iconPosition: IconPositionEnum.RIGHT,
    clickable: false,
    dashedBorder: true,
  },
  {
    type: PingPongButtonTypeEnum.CONTACT_ADMIN,
    color: BUTTON_COLOR.PRIMARY,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.ASK_CHANGE_DATES,
    color: BUTTON_COLOR.LIGHT_COLOR,
    weight: BUTTON_WEIGHT.OUTLINED,
    icon: <IconSvg.Chat className={s.iconChat} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.WAITING_STEP_ONE,
    color: BUTTON_COLOR.LIGHT_DOUBLE_GREY,
    weight: BUTTON_WEIGHT.OUTLINED,
    icon: <IconSvg.Loader color='grey' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: false,
  },
  {
    type: PingPongButtonTypeEnum.DECLINED_BY_OWNER,
    color: BUTTON_COLOR.RED,
    weight: BUTTON_WEIGHT.OUTLINED,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.CONFIRMED_BY_OWNER,
    color: BUTTON_COLOR.GREEN,
    weight: BUTTON_WEIGHT.OUTLINED,
    icon: <Icon name='check-circle-outline' className={s.icon} />,
    iconPosition: IconPositionEnum.RIGHT,
    clickable: false,
    dashedBorder: true,
  },
  {
    type: PingPongButtonTypeEnum.HOUSE_NOT_AVAILABLE,
    color: BUTTON_COLOR.COLOR,
    weight: BUTTON_WEIGHT.OUTLINED,
    clickable: true,
  },
  {
    type: PingPongButtonTypeEnum.DECLINED_BY_USER_ADMIN,
    color: BUTTON_COLOR.LIGHT_DOUBLE_GREY,
    icon: <Icon name='lock' className={s.icon} />,
    iconPosition: IconPositionEnum.LEFT,
    clickable: false,
  },
]

export interface IPingPongButtonProps extends IPingPongButtonData {
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  label: ReactNode
  isLoading?: boolean
  dataTestId?: string
  className?: string
}

const GenericPingPongButton = ({
  color,
  weight,
  label,
  icon,
  iconPosition,
  onClick,
  disabled,
  clickable,
  dashedBorder,
  type,
  isLoading = false,
  dataTestId,
  className,
}: IPingPongButtonProps) => {
  if (disabled) {
    color = BUTTON_COLOR.BLACK
    icon = <IconSvg.Lock color='black' />
    iconPosition = IconPositionEnum.LEFT
    clickable = false
  }

  return (
    <Button
      dataMixpanel={{ name: 'ping-pong-button', data: { type: type, label } }}
      color={color}
      weight={weight}
      onClick={(e) => {
        e.stopPropagation()
        onClick && onClick()
      }}
      disabled={isLoading || disabled}
      className={clsx(
        s.button,
        dashedBorder && s.dashedBorder,
        !clickable && s.notClickable,
        className,
      )}
      loading={isLoading}
      data-testid={dataTestId}
    >
      {icon && iconPosition === IconPositionEnum.LEFT && (
        <div className={clsx(s.iconWrapper, s.left)}>{icon}</div>
      )}
      {label}
      {icon && iconPosition === IconPositionEnum.RIGHT && (
        <div className={clsx(s.iconWrapper, s.right)}>{icon}</div>
      )}
    </Button>
  )
}

const PingPongButton = ({
  onClick,
  type,
  label,
  canUpdateProposal,
  isLoading,
  dataTestId,
  hideIcon,
  className,
}: {
  onClick?: () => void
  type: PingPongButtonTypeEnum
  label: ReactNode
  canUpdateProposal?: boolean | null | undefined
  isLoading?: boolean
  dataTestId?: string
  hideIcon?: boolean
  className?: string
}) => {
  const { adminCanUpdate } = useAdminCanUpdate()
  const { handleErrorBeforePingPongAction } = useUserCanUpdateProposal()

  const pingPongButtonData = PingPongButtonData.find(
    (buttonData) => buttonData.type === type,
  )
  if (!pingPongButtonData) {
    return null
  }

  return (
    <GenericPingPongButton
      {...pingPongButtonData}
      icon={hideIcon ? undefined : pingPongButtonData.icon}
      onClick={() => {
        const { stopExecution } = handleErrorBeforePingPongAction()
        if (!stopExecution && onClick) {
          onClick()
        }
      }}
      disabled={adminCanUpdate === false || canUpdateProposal === false}
      label={label}
      isLoading={isLoading}
      dataTestId={dataTestId}
      className={className}
    />
  )
}

export default PingPongButton
